// Self-Service Registration Component - Updated for Phase 2
// File: guest-frontend/src/components/SelfServiceRegistration.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './SelfServiceRegistration.css';
import { API_BASE_URL, SERVICE_BASE_URL } from '../utils/apiBase';

export default function SelfServiceRegistration({
  eventConfig: incomingEventConfig = null,
  eventId: incomingEventId = null,
  prefetchedEvent = null
} = {}) {
  const { eventPath } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registrationStage = useMemo(() => {
    const stageParam = (searchParams.get('stage') || '').toLowerCase();
    return stageParam === 'onsite' ? 'onsite' : 'pre-event';
  }, [searchParams]);
  const redirectTimerRef = useRef(null);
  const [event, setEvent] = useState(() => {
    if (prefetchedEvent) return prefetchedEvent;
    if (incomingEventConfig && incomingEventId) {
      return {
        id: incomingEventId,
        name: incomingEventConfig.eventTitle,
        registration_mode: incomingEventConfig.registrationMode || 'standard',
        self_service_enabled: true,
        ...prefetchedEvent
      };
    }
    return null;
  });
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    guest_type: 'Single',
    email: '',
    phone: '',
    menu_selection: {}
  });
  const [members, setMembers] = useState([]);
  
  const [menuOptions, setMenuOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [eventLoading, setEventLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registrationDetails, setRegistrationDetails] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => () => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (prefetchedEvent) {
      setEvent(prefetchedEvent);
      setEventLoading(false);
      return;
    }
    loadEventByPath();
  }, [eventPath, prefetchedEvent]);

  useEffect(() => {
    if (event?.id) {
      loadMenuOptions();
    }
  }, [event]);

  useEffect(() => {
    const defaultLast = (formData.last_name || '').trim();
    if (!defaultLast) return;
    setMembers(prev => prev.map(member => (
      member.last_name ? member : { ...member, last_name: defaultLast }
    )));
  }, [formData.last_name]);

  const loadEventByPath = async () => {
    if (prefetchedEvent) return; // already provided
    setEventLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/events/by-path/${eventPath || 'default'}`);
      setEvent(response.data);
      
      if (!response.data.self_service_enabled) {
        setMessage('Self-service registration is not enabled for this event.');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Event not found or unavailable.');
      setMessageType('error');
    } finally {
      setEventLoading(false);
    }
  };

  const registrationMode = (event?.registration_mode || incomingEventConfig?.registrationMode || 'standard').toLowerCase();
  const isHouseholdEvent = registrationMode === 'household';

  const normalizeMenuOptions = (input) => {
    const source = input || [];
    const arr = Array.isArray(source) ? source
      : Array.isArray(source?.options) ? source.options
      : Array.isArray(source?.data) ? source.data
      : [];
    return arr.map(opt => {
      const id = opt.id ?? opt.option_id ?? Math.random().toString(36).slice(2);
      const limit = opt.servings_limit != null && !Number.isNaN(Number(opt.servings_limit))
        ? Number(opt.servings_limit)
        : null;
      const used = opt.servings_used != null && !Number.isNaN(Number(opt.servings_used))
        ? Number(opt.servings_used)
        : null;
      const remainingRaw = opt.servings_remaining != null && !Number.isNaN(Number(opt.servings_remaining))
        ? Number(opt.servings_remaining)
        : null;
      const remaining = limit != null
        ? (remainingRaw != null ? remainingRaw : (used != null ? Math.max(limit - used, 0) : null))
        : null;
      const depleted = Boolean(opt.is_depleted) || (limit != null && remaining !== null && remaining <= 0);
      return {
        id,
        name: opt.name || opt.title || 'Option',
        description: opt.description || '',
        items: Array.isArray(opt.items) ? opt.items : [],
        servingsLimit: limit,
        servingsUsed: used,
        servingsRemaining: remaining,
        isDepleted: depleted
      };
    });
  };

  const loadMenuOptions = async () => {
    if (!event?.id) return;
    
    try {
      const response = await axios.get(`${SERVICE_BASE_URL}/menu/options/active?event_id=${event.id}`);
      setMenuOptions(normalizeMenuOptions(response.data));
    } catch (error) {
      console.error('Error loading menu options:', error);
      setMenuOptions([]);
    }
  };

  useEffect(() => {
    setFormData(prev => {
      if (!prev?.menu_selection) return prev;
      const updatedSelection = { ...prev.menu_selection };
      let changed = false;
      const removedNames = [];
      menuOptions.forEach(opt => {
        if (opt.isDepleted && Object.prototype.hasOwnProperty.call(updatedSelection, opt.id)) {
          delete updatedSelection[opt.id];
          changed = true;
          removedNames.push(opt.name || 'Menu option');
        }
      });
      if (changed) {
        setMessage(`${removedNames.join(', ')} ${removedNames.length === 1 ? 'is' : 'are'} no longer available. Please choose another option.`);
        setMessageType('warn');
      }
      return changed ? { ...prev, menu_selection: updatedSelection } : prev;
    });
  }, [menuOptions]);

  const createMemberEntry = (defaults = {}) => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    first_name: defaults.first_name || '',
    last_name: defaults.last_name || ''
  });

  const requiresPartner = isHouseholdEvent && formData.guest_type === 'Couple';
  const guestTypeOptions = useMemo(() => ([
    { value: 'Single', label: 'Single', hint: 'Just me' },
    { value: 'Couple', label: 'Couple', hint: isHouseholdEvent ? 'Primary + partner' : 'Two guests' },
    { value: 'Family', label: isHouseholdEvent ? 'Household (3+)' : 'Family (3+)', hint: isHouseholdEvent ? 'Optional extra family members' : 'Bring the entire household' }
  ]), [isHouseholdEvent]);

  useEffect(() => {
    if (!isHouseholdEvent) return;
    setFormData(prev => (prev.guest_type === 'Couple' ? prev : { ...prev, guest_type: 'Couple' }));
  }, [isHouseholdEvent]);

  useEffect(() => {
    if (!requiresPartner) return;
    const defaultLast = (formData.last_name || '').trim();
    setMembers((prev) => {
      if (!prev.length) return [createMemberEntry({ last_name: defaultLast })];
      if (prev.length > 1) return prev.slice(0, 1);
      return prev;
    });
  }, [requiresPartner, formData.last_name]);

  const handleGuestTypeChange = (value) => {
    setFormData(prev => ({ ...prev, guest_type: value }));
    setMembers(prev => {
      const defaultLast = (formData.last_name || '').trim();
      if (value === 'Single') {
        return [];
      }
      if (value === 'Couple') {
        if (!prev.length) return [createMemberEntry({ last_name: defaultLast })];
        return prev.slice(0, 1);
      }
      return prev.length ? prev : [createMemberEntry({ last_name: defaultLast })];
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'guest_type') {
      handleGuestTypeChange(value);
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addMember = () => {
    const defaultLast = (formData.last_name || '').trim();
    setMembers(prev => [...prev, createMemberEntry({ last_name: defaultLast })]);
  };

  const removeMember = (id) => {
    setMembers(prev => prev.filter(member => member.id !== id));
  };

  const updateMember = (id, field, value) => {
    setMembers(prev => prev.map(member => (
      member.id === id ? { ...member, [field]: value } : member
    )));
  };

  const buildShareMessage = (card) => {
    const eventName = event?.name ? `${event.name}` : 'our event';
    if (card?.scan_url) {
      return `Here is your digital pass for ${eventName}: ${card.scan_url}`;
    }
    return `You're registered for ${eventName}!`;
  };

  const downloadCardImage = (card, filename = 'event-pass') => {
    if (!card?.qr_code) return;
    try {
      const link = document.createElement('a');
      link.href = card.qr_code;
      link.download = `${filename}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed', error);
      window.alert('Unable to download the QR code.');
    }
  };

  const shareViaWhatsapp = (card) => {
    if (!card?.scan_url) return;
    const message = buildShareMessage(card);
    const shareUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(shareUrl, '_blank', 'noopener');
  };

  const shareViaEmail = (card) => {
    if (!card?.scan_url) return;
    const subject = event?.name ? `${event.name} - Your Event Pass` : 'Your Event Pass';
    const message = buildShareMessage(card);
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
  };

  const shareNative = async (card) => {
    if (!card?.scan_url) return shareViaWhatsapp(card);
    const message = buildShareMessage(card);
    if (navigator.share) {
      try {
        await navigator.share({
          title: event?.name || 'Event Pass',
          text: message,
          url: card.scan_url
        });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.warn('Native share failed, falling back to WhatsApp', error);
      }
    }
    shareViaWhatsapp(card);
  };

  const copyLinkToClipboard = async (card) => {
    if (!card?.scan_url) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(card.scan_url);
        window.alert('Link copied to clipboard!');
      } else {
        window.prompt('Copy this link:', card.scan_url);
      }
    } catch (error) {
      console.warn('Clipboard write failed', error);
      window.prompt('Copy this link:', card.scan_url);
    }
  };

  const renderCardActions = (card, filename = 'event-pass') => (
    <div className="card-share-actions">
      {card?.qr_code && (
        <button type="button" onClick={() => downloadCardImage(card, filename)}>
          Save QR
        </button>
      )}
      {card?.scan_url && (
        <>
          <button type="button" onClick={() => shareViaWhatsapp(card)}>Share on WhatsApp</button>
          <button type="button" onClick={() => shareViaEmail(card)}>Send via Email</button>
          <button type="button" onClick={() => copyLinkToClipboard(card)}>Copy Link</button>
          {typeof navigator !== 'undefined' && navigator.share && (
            <button type="button" onClick={() => shareNative(card)}>Share</button>
          )}
        </>
      )}
    </div>
  );

  const handleMenuSelection = (optionId, itemId) => {
    const option = menuOptions.find(opt => opt.id === optionId);
    if (option?.isDepleted) {
      setMessage('That menu option is no longer available. Please choose another selection.');
      setMessageType('warn');
      return;
    }
    setFormData(prev => ({
      ...prev,
      menu_selection: {
        ...prev.menu_selection,
        [optionId]: itemId
      }
    }));
  };

  const resetForm = () => {
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
    setRedirecting(false);
    setRegistrationDetails(null);
    setFormData({
      first_name: '',
      last_name: '',
      guest_type: isHouseholdEvent ? 'Couple' : 'Single',
      email: '',
      phone: '',
      menu_selection: {}
    });
    setMembers([]);
    setMessage('');
    setRegistrationComplete(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const firstName = (formData.first_name || '').trim();
    const lastName = (formData.last_name || '').trim();

    if (!firstName || !lastName) {
      setMessage('Please enter both your first and last name.');
      setMessageType('error');
      return;
    }

    const depletedSelected = menuOptions.some(opt => opt.isDepleted && formData.menu_selection[opt.id]);
    if (depletedSelected) {
      setMessage('One or more of your selected menu options is no longer available. Please update your selections.');
      setMessageType('error');
      return;
    }

    const seenKeys = new Set();
    let normalizedMembers = members
      .map(m => ({
        first_name: (m.first_name || '').trim(),
        last_name: (m.last_name || '').trim()
      }))
      .filter(m => {
        if (!m.first_name || !m.last_name) return false;
        const key = `${m.first_name.toLowerCase()}|${m.last_name.toLowerCase()}`;
        const primaryKey = `${firstName.toLowerCase()}|${lastName.toLowerCase()}`;
        if (key === primaryKey) return false;
        if (seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
      });

    if (formData.guest_type === 'Single') {
      normalizedMembers = [];
    } else if (formData.guest_type === 'Couple' && normalizedMembers.length > 1) {
      normalizedMembers = normalizedMembers.slice(0, 1);
    }

    if (requiresPartner && normalizedMembers.length === 0) {
      setMessage('Please add your partner\'s first and last name so we can reserve both seats together.');
      setMessageType('error');
      return;
    }
    
    setLoading(true);
    setMessage('');
    setRedirecting(false);
    setRegistrationDetails(null);
    setRegistrationComplete(false);

    try {
      const payload = {
        guest_name: `${firstName} ${lastName}`.trim(),
        first_name: firstName,
        last_name: lastName,
        guest_type: formData.guest_type || 'Single',
        members: normalizedMembers,
        menu_selection: formData.menu_selection,
        email: formData.email ? formData.email.trim() : undefined,
        phone: formData.phone ? formData.phone.trim() : undefined,
        registration_stage: registrationStage
      };
      const response = await axios.post(`${API_BASE_URL}/events/${event.id}/self-register`, payload);
      const registrationPayload = response.data || {};

      setRegistrationDetails(registrationPayload);
      setMessage(registrationStage === 'onsite'
        ? 'Registration successful! Taking you to check-in now.'
        : 'Registration successful! We\'ll see you at the event.');
      setMessageType('success');
      setRegistrationComplete(true);
      if (registrationStage === 'onsite') {
        setRedirecting(true);
        const redirectPath = registrationPayload.redirect_path || (eventPath ? `/${eventPath}` : '/');
        if (redirectTimerRef.current) {
          clearTimeout(redirectTimerRef.current);
        }
        redirectTimerRef.current = setTimeout(() => {
          navigate(`${redirectPath}?registered=1&stage=${registrationStage}`, {
            replace: true,
            state: {
              registeredGuest: registrationPayload.guest || null,
              registrationStage
            }
          });
        }, 1200);
      } else {
        setRedirecting(false);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        const detail = error.response.data?.error || error.response.data?.message;
        setMessage(detail || 'Invalid registration data.');
      } else {
        setMessage('Registration failed. Please try again.');
      }
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  if (eventLoading) {
    return (
      <div className="self-service-container">
        <div className="registration-loading">
          <div className="loading-spinner"></div>
          <p>Loading event information...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="self-service-container">
        <div className="registration-header">
          <h2>⚠️ Event Not Found</h2>
          <p>The requested event could not be found.</p>
        </div>
        {message && (
          <div className={`registration-message error`}>
            ❌ {message}
          </div>
        )}
        <button onClick={() => navigate('/')} className="btn-back-home">
          Back to Home
        </button>
      </div>
    );
  }

  if (!event.self_service_enabled) {
    return (
      <div className="self-service-container">
        <div className="registration-header">
          <h2>⚠️ Registration Unavailable</h2>
          <p>Self-service registration is not available for <strong>{event.name}</strong>.</p>
          <p>Please contact event staff for assistance with registration.</p>
        </div>
        <button onClick={() => navigate(`/${eventPath}`)} className="btn-back-checkin">
          Go to Check-in
        </button>
      </div>
    );
  }

  if (registrationComplete) {
    const cards = registrationDetails?.cards || {};
    const primaryCardInfo = cards?.primary || null;
    const memberCards = Array.isArray(cards?.members)
      ? cards.members.filter(card => card && (card.qr_code || card.scan_url))
      : [];
    const household = registrationDetails?.household || null;
    const primaryFirst =
      household?.primary?.first_name ||
      registrationDetails?.guest?.first_name ||
      formData.first_name;
    const primaryLast =
      household?.primary?.last_name ||
      registrationDetails?.guest?.last_name ||
      formData.last_name;
    const displayName = [primaryFirst, primaryLast].filter(Boolean).join(' ') || 'Guest';
    const displayType = (registrationDetails?.guest?.type || registrationDetails?.guest_type || formData.guest_type || 'Single');
    const displayEmail = registrationDetails?.email || formData.email || '';
    const displayPhone = registrationDetails?.phone || formData.phone || '';
    const householdMembers = Array.isArray(household?.members) ? household.members : [];
    const partySize = registrationDetails?.household_size || (1 + householdMembers.length);
    return (
      <div className="self-service-container">
        <div className="registration-success">
          <div className="success-icon">🎉</div>
          <h2>Registration Complete!</h2>
          <p>
            {registrationStage === 'onsite'
              ? 'Thanks for registering on-site.'
              : <>Welcome to <strong>{event.name}</strong>!</>}
          </p>
          {registrationStage !== 'onsite' && (
            <p className="pre-event-note">
              No need to check in yet—just give your name when you arrive and we’ll seat your party.
            </p>
          )}
          <div className="registration-details">
            <div className="detail-item">
              <strong>Primary Guest:</strong> {displayName || 'Guest'}
            </div>
            <div className="detail-item">
              <strong>Guest Type:</strong> {displayType}
            </div>
            <div className="detail-item">
              <strong>Party Size:</strong> {partySize}
            </div>
            {displayEmail && (
              <div className="detail-item">
                <strong>Email:</strong> {displayEmail}
              </div>
            )}
            {displayPhone && (
              <div className="detail-item">
                <strong>Phone:</strong> {displayPhone}
              </div>
            )}
            {Object.keys(formData.menu_selection).length > 0 && (
              <div className="detail-item">
                <strong>Menu Selections:</strong>
                <ul className="menu-list">
                  {menuOptions
                    .filter(option => formData.menu_selection[option.id])
                    .map(option => {
                      const selectedItem = option.items?.find(item => 
                        item.id === formData.menu_selection[option.id]
                      );
                      return (
                        <li key={option.id}>
                          <strong>{option.name}:</strong> {selectedItem?.name || 'Selected'}
                        </li>
                      );
                    })
                  }
                </ul>
              </div>
            )}
            {householdMembers.length > 0 && (
              <div className="detail-item">
                <strong>Additional Members:</strong>
                <ul className="members-list">
                  {householdMembers.map((member, idx) => (
                    <li key={`${member.first_name}-${member.last_name}-${idx}`}>
                      {[member.first_name, member.last_name].filter(Boolean).join(' ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {primaryCardInfo && (primaryCardInfo.qr_code || primaryCardInfo.scan_url) && (
            <div className="card-share-section">
              <h4>Your Digital Pass</h4>
              {primaryCardInfo.qr_code ? (
                <img
                  src={primaryCardInfo.qr_code}
                  alt="Event pass QR code"
                  className="card-share-qr"
                />
              ) : (
                <div className="card-share-placeholder">
                  Your QR code will be available shortly.
                </div>
              )}
              {primaryCardInfo.scan_url && (
                <p className="card-share-link">
                  Pass URL:&nbsp;
                  <a href={primaryCardInfo.scan_url} target="_blank" rel="noopener noreferrer">
                    {primaryCardInfo.scan_url}
                  </a>
                </p>
              )}
              {renderCardActions(primaryCardInfo, 'event-pass')}
            </div>
          )}
          {memberCards.length > 0 && (
            <div className="card-share-members">
              <h4>Household Passes</h4>
              <div className="card-share-grid">
                {memberCards.map((memberCard, idx) => {
                  const memberName = [memberCard?.first_name, memberCard?.last_name]
                    .filter(Boolean)
                    .join(' ') || `Member ${idx + 1}`;
                  const downloadLabel = memberCard?.card_identifier
                    ? `member-pass-${memberCard.card_identifier}`
                    : `member-pass-${idx + 1}`;
                  return (
                    <div key={memberCard?.guest_id || idx} className="card-share-card">
                      <div className="card-share-member-name">{memberName}</div>
                      {memberCard?.qr_code ? (
                        <img
                          src={memberCard.qr_code}
                          alt={`${memberName} event pass`}
                          className="card-share-qr"
                        />
                      ) : (
                        <div className="card-share-placeholder">
                          QR code will be available soon.
                        </div>
                      )}
                      {memberCard?.scan_url && (
                        <p className="card-share-link">
                          <a href={memberCard.scan_url} target="_blank" rel="noopener noreferrer">
                            {memberCard.scan_url}
                          </a>
                        </p>
                      )}
                      {renderCardActions(memberCard, downloadLabel)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {redirecting && (
            <div className="redirect-note">
              Please hold on—redirecting you to the check-in screen…
            </div>
          )}
          <div className="success-actions">
            <button onClick={resetForm} className="btn-register-another" disabled={redirecting}>
              Register Another Guest
            </button>
            {registrationStage === 'onsite' && (
              <button 
                onClick={() => navigate(`/${eventPath}?mode=program`)}
                className="btn-view-program"
                disabled={redirecting}
              >
                View Event Program
              </button>
            )}
            {registrationStage === 'onsite' && (
              <button 
                onClick={() => navigate(`/${eventPath}`)}
                className="btn-back-checkin"
                disabled={redirecting}
              >
                Back to Check-in
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="self-service-container">
      <div className="registration-header">
        <h2>{registrationStage === 'onsite' ? '🚪 On-site Registration' : '🆕 Guest Registration'}</h2>
        <h3>{event.name}</h3>
        <p className="stage-pill">
          {registrationStage === 'onsite'
            ? 'Register here, then head straight to check-in.'
            : 'Reserve your spot before you arrive.'}
        </p>
        {event.description && <p className="event-description">{event.description}</p>}
        {event.event_date && (
          <p className="event-date">
            {new Date(event.event_date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        )}
      </div>

      {message && (
        <div className={`registration-message ${messageType}`}>
          {messageType === 'success' && '✅ '}
          {messageType === 'error' && '❌ '}
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="registration-form">
        {/* Guest Name */}
        <div className="form-row">
          <div className="form-section form-half">
            <label htmlFor="first_name">First Name *</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              placeholder="Enter first name"
              required
              disabled={loading}
            />
          </div>
          <div className="form-section form-half">
            <label htmlFor="last_name">Last Name *</label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              placeholder="Enter last name"
              required
              disabled={loading}
            />
          </div>
        </div>

        {isHouseholdEvent && (
          <div className="household-banner" role="status">
            <strong>Household seating</strong>
            <p>Register both the primary guest and partner so we can reserve two seats together. Select “Single” only if you’re attending alone.</p>
          </div>
        )}

        {/* Guest Type */}
        <div className="form-section">
          <label>Guest Type *</label>
          <div className="guest-type-options">
            {guestTypeOptions.map(option => (
              <label key={option.value} className="guest-type-option">
                <input
                  type="radio"
                  name="guest_type"
                  value={option.value}
                  checked={formData.guest_type === option.value}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                <span>
                  {option.label}
                  <small>{option.hint}</small>
                </span>
              </label>
            ))}
          </div>
          <small>
            {isHouseholdEvent
              ? 'Keep “Couple” selected if you have a partner switching badges with you.'
              : 'We’ll use this to plan seating; your table will be assigned when you check in.'}
          </small>
        </div>

        {(formData.guest_type === 'Couple' || formData.guest_type === 'Family') && (
          <div className="household-section">
            <div className="household-header">
              <h4>{formData.guest_type === 'Couple' ? 'Partner Details' : 'Household Members'}</h4>
              <p>
                {formData.guest_type === 'Couple'
                  ? 'Add your partner so we can keep you together.'
                  : 'List each family member joining you. Leave blank if you are registering alone.'}
              </p>
            </div>
            {members.length === 0 && (
              <div className="household-empty">
                {requiresPartner
                  ? 'Partner details are required for this event. Please add their first and last name.'
                  : 'You can add additional names, or leave this blank to register just yourself.'}
              </div>
            )}
            {members.map((member, index) => (
              <div key={member.id} className="member-row">
                <div className="form-section form-half">
                  <label htmlFor={`member-first-${member.id}`}>First Name</label>
                  <input
                    type="text"
                    id={`member-first-${member.id}`}
                    value={member.first_name}
                    onChange={(e) => updateMember(member.id, 'first_name', e.target.value)}
                    placeholder="First name"
                    disabled={loading}
                  />
                </div>
                <div className="form-section form-half">
                  <label htmlFor={`member-last-${member.id}`}>Last Name</label>
                  <input
                    type="text"
                    id={`member-last-${member.id}`}
                    value={member.last_name}
                    onChange={(e) => updateMember(member.id, 'last_name', e.target.value)}
                    placeholder="Last name"
                    disabled={loading}
                  />
                </div>
                <button
                  type="button"
                  className="remove-member-btn"
                  onClick={() => removeMember(member.id)}
                  disabled={loading || (requiresPartner && members.length <= 1 && formData.guest_type === 'Couple')}
                  aria-label={`Remove member ${index + 1}`}
                >
                  Remove
                </button>
              </div>
            ))}
            <div className="household-actions">
              <button
                type="button"
                className="btn-add-member"
                onClick={addMember}
                disabled={loading || (requiresPartner && members.length >= 1)}
              >
                {formData.guest_type === 'Couple' ? 'Add Partner' : 'Add Family Member'}
              </button>
            </div>
          </div>
        )}

        {/* Contact Info */}
        <div className="form-section">
          <label htmlFor="email">Email (optional)</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="you@example.com"
            disabled={loading}
            autoComplete="email"
          />
        </div>

        <div className="form-section">
          <label htmlFor="phone">Phone (optional)</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="Phone number"
            disabled={loading}
            autoComplete="tel"
          />
        </div>

        {/* Menu Options */}
        {menuOptions.length > 0 && (
          <div className="form-section">
            <h4>Menu Preferences</h4>
            <p>Select your preferred options for each course:</p>
            
            {menuOptions.map(option => {
              const optionDepleted = option.isDepleted;
              const limit = option.servingsLimit;
              const remaining = option.servingsRemaining;
              return (
                <div
                  key={option.id}
                  className={`menu-option${optionDepleted ? ' menu-option--depleted' : ''}`}
                >
                  <label className="option-label">
                    {option.name}
                    {typeof limit === 'number' && (
                      <span className="menu-option-meta">
                        {remaining != null
                          ? `${remaining} of ${limit} servings remaining`
                          : `${limit} servings planned`}
                      </span>
                    )}
                  </label>
                  <div className="menu-items">
                    {option.items?.map(item => (
                      <label key={item.id} className="menu-item">
                        <input
                          type="radio"
                          name={`menu_${option.id}`}
                          value={item.id}
                          checked={formData.menu_selection[option.id] === item.id}
                          onChange={() => handleMenuSelection(option.id, item.id)}
                          disabled={loading || optionDepleted}
                        />
                        <span className="item-name">{item.name}</span>
                        {item.description && (
                          <span className="item-description">{item.description}</span>
                        )}
                      </label>
                    ))}
                  </div>
                  {optionDepleted && (
                    <div className="menu-option-warning">
                      This option has reached its serving limit and is no longer available.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Submit Button */}
        <div className="form-actions">
          <button type="submit" disabled={loading} className="btn-register">
            {loading ? (
              <>
                <div className="btn-spinner"></div>
                Registering...
              </>
            ) : (
              'Complete Registration'
            )}
          </button>
          {registrationStage === 'onsite' && (
            <button 
              type="button" 
              onClick={() => navigate(`/${eventPath}`)} 
              className="btn-back"
              disabled={loading}
            >
              Back to Check-in
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
